import { z } from 'zod';
import { AuditSchema, IdSchema, InstantSchema } from './common.ts';
import { CostLineSchema, costTotals, type CostLine, type CostTotals } from './cost-line.ts';

/**
 * What a job is going to cost, and what the owner will be charged for it.
 *
 * One approved estimate becomes two things at once: the **original budget**
 * (the cost side) and the **initial contract** (the price side). That is
 * why approving one is a server operation rather than a field somebody
 * sets — it writes records in another collection, and a client that could
 * write those could approve its own estimate.
 *
 * **Versions are separate documents, not a history array.** An estimate is
 * re-priced three or four times before anybody agrees to it, and each
 * version is a thing somebody was shown. Keeping them as documents means a
 * version can be opened, compared and printed; keeping them as an array
 * inside one document means a 400-line estimate carries four copies of
 * itself and eventually exceeds what Firestore will store.
 */
export const ESTIMATE_STATUSES = ['draft', 'approved', 'superseded', 'void'] as const;
export const EstimateStatusSchema = z.enum(ESTIMATE_STATUSES);
export type EstimateStatus = z.infer<typeof EstimateStatusSchema>;

/**
 * Which transitions are legal.
 *
 * Narrow on purpose. The one that looks missing is `approved → draft`:
 * un-approving an estimate would leave the budget it created pointing at
 * nothing, and the honest way back is a new version. `superseded` is what
 * an older approved estimate becomes when a newer one is approved, so the
 * history reads as a sequence rather than as several live contracts.
 */
const TRANSITIONS: Record<EstimateStatus, readonly EstimateStatus[]> = {
  draft: ['approved', 'void'],
  approved: ['superseded', 'void'],
  superseded: [],
  void: [],
};

export function canTransitionEstimate(from: EstimateStatus, to: EstimateStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

/** Whether this estimate can still be edited. */
export function isEstimateEditable(status: EstimateStatus): boolean {
  // Only a draft. An approved estimate is a contract, and a superseded or
  // void one is a record of what was true — editing either would rewrite
  // what somebody agreed to.
  return status === 'draft';
}

export const EstimateApprovalSchema = z.object({
  by: IdSchema,
  at: InstantSchema,
  /** What the totals were when it was approved, frozen. */
  cost: z.object({ amount: z.number().int(), currency: z.string().length(3) }),
  price: z.object({ amount: z.number().int(), currency: z.string().length(3) }),
});
export type EstimateApproval = z.infer<typeof EstimateApprovalSchema>;

export const EstimateSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  /** `Estimate 3`, in the words people use on the telephone. */
  version: z.number().int().positive(),
  title: z.string().trim().min(1).max(200),
  /** Shown to the owner on the proposal. Plain text until there is a sanitiser. */
  description: z.string().max(20_000).default(''),
  status: EstimateStatusSchema,
  lines: z.array(CostLineSchema).default([]),
  /**
   * What the approval recorded.
   *
   * The totals are **frozen into the approval** even though they are
   * derived everywhere else, and the exception is deliberate: an approved
   * estimate is a contract, and what somebody agreed to must not change
   * because a cost code's default markup was edited afterwards. Everywhere
   * the estimate is still a draft, the totals derive.
   */
  approval: EstimateApprovalSchema.optional(),
  /** Set when a later version takes over, so the chain is readable. */
  supersededById: IdSchema.optional(),
  /** Why it was voided, which is the useful part of voiding one. */
  reason: z.string().max(2_000).optional(),
  tagIds: z.array(IdSchema).default([]),
});
export type Estimate = z.infer<typeof EstimateSchema>;

/**
 * What an estimate comes to.
 *
 * Derived from the lines while it is a draft; **read from the approval**
 * once there is one. A customer who signed for $10,968 must keep seeing
 * $10,968 even if somebody later corrects a catalog price, and a screen
 * that recomputed the total would quietly restate a signed contract.
 */
export function estimateTotals(estimate: Estimate, currency = 'USD'): CostTotals {
  const derived = costTotals(estimate.lines, currency);
  if (!estimate.approval) return derived;
  return {
    ...derived,
    cost: estimate.approval.cost,
    price: estimate.approval.price,
    markup: {
      amount: estimate.approval.price.amount - estimate.approval.cost.amount,
      currency: estimate.approval.price.currency,
    },
  };
}

/**
 * Whether what is on screen still matches what was approved.
 *
 * `false` means somebody edited an approved estimate's lines, which the
 * rules and the use case both refuse — so this is a check against a bug
 * rather than against a person, and the budget page shows it as a warning
 * rather than silently preferring one number.
 */
export function approvalMatchesLines(estimate: Estimate, currency = 'USD'): boolean {
  if (!estimate.approval) return true;
  const derived = costTotals(estimate.lines, currency);
  return (
    derived.cost.amount === estimate.approval.cost.amount &&
    derived.price.amount === estimate.approval.price.amount
  );
}

/** The one that counts: the approved estimate, if there is one. */
export function approvedEstimate(estimates: readonly Estimate[]): Estimate | null {
  return estimates.find((estimate) => estimate.status === 'approved') ?? null;
}

/**
 * The next version number for a job.
 *
 * Highest existing plus one, not the count plus one. An estimate that was
 * voided still used its number, and reusing it would make two different
 * documents both "version 3" in somebody's email.
 */
export function nextEstimateVersion(estimates: readonly Estimate[]): number {
  return estimates.reduce((highest, estimate) => Math.max(highest, estimate.version), 0) + 1;
}

/** Estimates newest version first, which is the order anybody wants them. */
export function sortEstimates(estimates: readonly Estimate[]): Estimate[] {
  return [...estimates].sort((a, b) => b.version - a.version);
}

export interface EstimateLineChange {
  readonly kind: 'added' | 'removed' | 'changed';
  readonly title: string;
  readonly before?: CostLine;
  readonly after?: CostLine;
}

/**
 * What changed between two versions.
 *
 * Matched by line **id**, not by title: re-pricing a line keeps its id, and
 * matching by title would report a renamed line as one removed and one
 * added — which is exactly the pair a person is trying to tell apart when
 * they ask what changed.
 */
export function diffEstimates(
  before: Pick<Estimate, 'lines'>,
  after: Pick<Estimate, 'lines'>,
): readonly EstimateLineChange[] {
  const beforeById = new Map(before.lines.map((line) => [line.id, line] as const));
  const afterById = new Map(after.lines.map((line) => [line.id, line] as const));
  const changes: EstimateLineChange[] = [];

  for (const line of after.lines) {
    const previous = beforeById.get(line.id);
    if (!previous) {
      changes.push({ kind: 'added', title: line.title, after: line });
      continue;
    }
    if (JSON.stringify(previous) !== JSON.stringify(line)) {
      changes.push({ kind: 'changed', title: line.title, before: previous, after: line });
    }
  }

  for (const line of before.lines) {
    if (!afterById.has(line.id)) {
      changes.push({ kind: 'removed', title: line.title, before: line });
    }
  }

  return changes;
}
